import type { ReactElement } from "react";
import type { LamaMaturityHierarchyView } from "@playbookai/widget-schemas";
import { KPIValue } from "../../atoms/KPIValue/index.js";
import { TrendIndicator } from "../../atoms/TrendIndicator/TrendIndicator.js";
import { MaturityRadar } from "../MaturityRadar/MaturityRadar.js";
import type { WidgetBodyProps } from "../types.js";
import {
  LAMA_SUPPRESSED_BODY,
  LAMA_SUPPRESSED_TITLE,
} from "../lama-report-presentation.js";
import {
  LAMA_HIERARCHY_VIEW_LABEL,
  LAMA_NO_BASELINE_COPY,
  LAMA_NO_RESPONSES_COPY,
  LAMA_PERCENT_MAX,
  axisIsScored,
  effectiveHierarchyView,
  hierarchyAxisRows,
  hierarchyAxisUnit,
  hierarchyCoverage,
  hierarchyHeadline,
  hierarchyMissingReason,
  lamaChangeDescription,
  lamaPercentLabel,
  lamaPointsLabel,
} from "../lama-hierarchy-presentation.js";

/** A profile needs at least this many scored axes to be a shape at all. */
const MIN_RADAR_AXES = 3;

/**
 * LAMA Maturity Overview (2026-09-10 ADR-0027 amendment) — the campaign's
 * Pillar → Category → Question results over the BOUNDED source pair: the
 * latest finalised baseline and the latest reported wave. Data-blind: every
 * percent, delta and count arrives from `lama/maturity-hierarchy@1`, computed
 * by the provider on the matched assessed scope; the widget only chooses which
 * of the payload's sides to draw, from the host's display-only `view`.
 *
 * Three things a reader must never be misled about:
 *  - an UNASSESSED axis is a gap on the radar and a dash in the table, never a
 *    zero — so the radar draws with `null` points and the coverage strip says
 *    how much of the scope each side covers;
 *  - the COMPARISON is on what both sides assessed, and says so;
 *  - a SUPPRESSED wave publishes nothing, and that is worded as anonymity, not
 *    as "no responses".
 */
export function LamaMaturityHierarchyWidget({
  data,
}: WidgetBodyProps<LamaMaturityHierarchyView>): ReactElement {
  const view = effectiveHierarchyView(data);
  const requested = data.view ?? "compare";
  const missing = hierarchyMissingReason(data, view);
  const coverage = hierarchyCoverage(data);
  const rows = hierarchyAxisRows(data, view);
  const headline = hierarchyHeadline(data, view);
  const unit = hierarchyAxisUnit(data);
  const scored = rows.filter((row) => axisIsScored(row, view));
  const unscored = rows.filter((row) => !axisIsScored(row, view));

  const viewNote =
    requested !== view
      ? `Showing ${LAMA_HIERARCHY_VIEW_LABEL[view]}: ${
          requested === "compare"
            ? "a comparison needs both a finalised baseline and a reported wave."
            : `${LAMA_HIERARCHY_VIEW_LABEL[requested]} is not available for this campaign.`
        }`
      : null;

  return (
    <div>
      {/* PROVENANCE IS THE HOST'S, ONCE. `hierarchyProvenance` used to render
          here as well as on the report's view-switch card and again on the
          results section, so one page carried the same two source strings three
          times over. The switch card renders whenever either hierarchy section
          resolved, so nothing is lost by naming the sources where the source is
          chosen. What stays is which view this section DREW — the switch shows
          what was asked for, and the two differ whenever a view falls back. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-pb-text-muted">
        <span data-testid="lama-hierarchy-view">
          Viewing:{" "}
          <span className="font-medium text-pb-text">
            {LAMA_HIERARCHY_VIEW_LABEL[view]}
          </span>
        </span>
      </div>
      {viewNote ? (
        <p role="status" className="mt-1 text-xs text-pb-text-muted">
          {viewNote}
        </p>
      ) : null}

      {missing === "suppressed" ? (
        <div className="mt-3 rounded-md border border-pb-border p-3">
          <p className="text-sm font-medium text-pb-text">
            {LAMA_SUPPRESSED_TITLE}
          </p>
          <p className="mt-1 text-sm text-pb-text-muted">
            {LAMA_SUPPRESSED_BODY}
          </p>
        </div>
      ) : missing === "no-baseline" ? (
        <p className="mt-3 text-sm text-pb-text-muted">
          {LAMA_NO_BASELINE_COPY}
        </p>
      ) : missing === "no-responses" ? (
        <p className="mt-3 text-sm text-pb-text-muted">
          {LAMA_NO_RESPONSES_COPY}
        </p>
      ) : (
        <>
          {/* THE ANSWER FIRST, THE CAVEAT UNDER IT. The coverage strip used to
              sit above the overall figures, so a reader met four counts before
              the number they came for. */}
          {headline ? (
            <div className="mt-3 flex flex-wrap items-end gap-x-6 gap-y-2">
              {/* The 30px hero figures are KPIValue lg (look row SN-16). */}
              {view !== "responses" ? (
                <KPIValue
                  variant="label"
                  size="lg"
                  label="Baseline overall"
                  value={lamaPercentLabel(headline.baseline)}
                />
              ) : null}
              {view !== "baseline" ? (
                <KPIValue
                  variant="label"
                  size="lg"
                  label="Responses overall"
                  value={lamaPercentLabel(headline.responses)}
                />
              ) : null}
              {view === "compare" ? (
                <div>
                  <p className="text-xs text-pb-text-muted">Change</p>
                  <div className="mt-2">
                    {headline.delta === null ? (
                      <span className="text-sm text-pb-text-muted">—</span>
                    ) : (
                      <TrendIndicator
                        delta={Math.round(headline.delta)}
                        unit=" pts"
                        aria-label={lamaChangeDescription(headline.delta)}
                      />
                    )}
                  </div>
                </div>
              ) : null}
              <p
                className="text-xs text-pb-text-muted"
                data-testid="lama-hierarchy-coverage"
              >
                {headline.coverage}
              </p>
            </div>
          ) : null}

          {/* The coverage strip: what each side covers of the scope, and the
              overlap the comparison stands on. */}
          <dl className="mt-3 grid grid-cols-2 gap-2 rounded-md border border-pb-border px-3 py-2 text-xs @lg/content:grid-cols-4">
            <div>
              <dt className="sr-only">Scope</dt>
              <dd className="text-pb-text">
                {coverage.categoryCount}{" "}
                {coverage.categoryCount === 1 ? "category" : "categories"} in
                scope
              </dd>
            </div>
            <div>
              <dt className="sr-only">Baseline coverage</dt>
              <dd className="text-pb-text">
                {coverage.baselineAssessed === null
                  ? "No finalised baseline"
                  : `${coverage.baselineAssessed} with baseline scores`}
              </dd>
            </div>
            <div>
              <dt className="sr-only">Responses coverage</dt>
              <dd className="text-pb-text">
                {coverage.responsesAssessed === null
                  ? "No response scores"
                  : `${coverage.responsesAssessed} with response scores`}
              </dd>
            </div>
            <div>
              <dt className="sr-only">Comparable</dt>
              <dd className="text-pb-text">
                {coverage.comparable === null
                  ? "Not comparable yet"
                  : `${coverage.comparable} comparable`}
              </dd>
            </div>
          </dl>

          {scored.length < MIN_RADAR_AXES ? (
            <p className="mt-4 text-sm text-pb-text-muted">
              Not enough scored {unit} for a profile — {scored.length} of{" "}
              {rows.length} {unit}{" "}
              {view === "compare" ? "are comparable" : "have a score"} in this
              view. The table below lists every one.
            </p>
          ) : (
            <div
              className="mt-4 overflow-x-auto overscroll-x-contain rounded-md pb-2 focus-ring"
              /* A horizontally scrollable region MUST be keyboard focusable or a
                 keyboard-only user cannot reach the off-screen part of the
                 radar (WCAG 2.1.1) — the same pattern as the maturity profile. */
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
              tabIndex={0}
              role="region"
              /* NOT "maturity profile" — the legacy Maturity Profile section
                 on the same page already owns that name, and two landmarks
                 with one name leave a screen-reader user cycling between two
                 radars they cannot tell apart. */
              aria-label="Scrollable maturity overview chart"
            >
              <MaturityRadar
                data={rows.map((row) => ({
                  label: row.label,
                  value: view === "baseline" ? row.baseline : row.responses,
                  ...(view === "compare" ? { previous: row.baseline } : {}),
                }))}
                max={LAMA_PERCENT_MAX}
                unit="%"
                /* HEIGHT IS THE ONLY LEVER ON THE RING'S SIZE. recharts takes
                   `min(width, height) / 2` as the polar radius, so a 1120px
                   card drew a 236px ring off a 360px box and threw the rest of
                   the width away. The cap keeps the extra width as symmetric
                   margin for twelve pillar labels rather than as a stretched
                   canvas. */
                height={460}
                className="mx-auto min-w-150 max-w-3xl"
                currentLabel={view === "baseline" ? "Baseline" : "Responses"}
                previousLabel="Baseline"
                ariaLabel={`Maturity profile — ${scored.length} of ${rows.length} ${unit} scored on a percent scale${
                  view === "compare" ? ", with a baseline overlay" : ""
                }${
                  unscored.length > 0
                    ? `; not scored: ${unscored.map((row) => row.label).join(", ")}`
                    : ""
                }.`}
              />
            </div>
          )}

          {/* eslint-disable-next-line ds/no-hand-built -- kept: a borderless results table inside the section; DataTable draws its own frame, header band and cell padding, and no approved look row lists the change. */}
          <table className="mt-4 w-full text-sm">
            <caption className="sr-only">
              {unit === "pillars" ? "Pillar" : "Category"} results —{" "}
              {LAMA_HIERARCHY_VIEW_LABEL[view]}
            </caption>
            <thead>
              <tr className="text-xs text-pb-text-muted">
                <th scope="col" className="py-1 text-start font-normal">
                  {unit === "pillars" ? "Pillar" : "Category"}
                </th>
                {view !== "responses" ? (
                  <th scope="col" className="py-1 text-end font-normal">
                    Baseline
                  </th>
                ) : null}
                {view !== "baseline" ? (
                  <th scope="col" className="py-1 text-end font-normal">
                    Responses
                  </th>
                ) : null}
                {view === "compare" ? (
                  <th scope="col" className="py-1 text-end font-normal">
                    Change
                  </th>
                ) : null}
                <th scope="col" className="py-1 text-end font-normal">
                  Coverage
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-pb-border">
                  <th
                    scope="row"
                    className="py-2 text-start font-medium text-pb-text"
                  >
                    {row.label}
                  </th>
                  {view !== "responses" ? (
                    <td className="py-2 text-end pb-tabular">
                      {row.baseline === null ? (
                        <span className="text-pb-text-muted">
                          <span aria-hidden="true">—</span>
                          <span className="sr-only">Not assessed</span>
                        </span>
                      ) : (
                        lamaPercentLabel(row.baseline)
                      )}
                    </td>
                  ) : null}
                  {view !== "baseline" ? (
                    <td className="py-2 text-end pb-tabular">
                      {row.responses === null ? (
                        <span className="text-pb-text-muted">
                          <span aria-hidden="true">—</span>
                          <span className="sr-only">Not assessed</span>
                        </span>
                      ) : (
                        lamaPercentLabel(row.responses)
                      )}
                    </td>
                  ) : null}
                  {view === "compare" ? (
                    <td className="py-2 text-end">
                      {row.delta === null ? (
                        <span className="text-pb-text-muted">
                          <span aria-hidden="true">—</span>
                          <span className="sr-only">Not comparable</span>
                        </span>
                      ) : (
                        <TrendIndicator
                          delta={Math.round(row.delta)}
                          unit=" pts"
                          aria-label={`${row.label}: ${lamaChangeDescription(row.delta)}`}
                        />
                      )}
                    </td>
                  ) : null}
                  <td className="py-2 text-end text-xs text-pb-text-muted">
                    {row.coverage}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-pb-text-muted">
            {view === "compare"
              ? "Comparison uses categories assessed in both, re-averaged on that shared scope. Change is in percentage points."
              : "Unassessed categories are left out of every mean, never counted as zero."}
          </p>
        </>
      )}
    </div>
  );
}

// Referenced for the module's public vocabulary; keeps the label helper live.
void lamaPointsLabel;
